// Simplified Chinese copy for /on-demand-ftp, translated from copy.en.ts. Same shape (checked by copy.test.ts).
// "按需 FTP", "网页上传工具", "工作区" and "您" as in the site's zh locale (src/i18n/locales/zh.json).
// The uploader's and FileZilla's own labels are in English, so «labels» are quoted as the app shows them.
import type { OnDemandFtpCopy } from "./copy.en";

const zh: OnDemandFtpCopy = {
  seo: {
    title: "按需 FTP 是如何运作的 | Photohawk",
    description:
      "如果一定要用 FTP，所有计划的工作区成员都可以在网页上传工具中启动自己的 Photohawk FTP 服务器。约 30 秒即可就绪，使用永不改变的个人地址，闲置一小时后会自动关闭。",
  },
  hero: {
    badge: "所有计划",
    eyebrow: "现已推出",
    title: "按需 FTP 是如何运作的",
    subtitle:
      "如果一定要用 FTP，您可以随时在网页上传工具中启动自己的 FTP 服务器。约 30 秒即可就绪，用完后会自动关闭。",
  },
  before: {
    title: "开始之前：网页上传工具更快",
    body: "网页上传工具是标准的上传方式，所有计划均可使用。",
    points: [
      { id: "parallel", text: "它可以同时发送大量照片，远多于 FileZilla（默认同时 2 张，最多不超过 10 张）这样的 FTP 软件。" },
      { id: "adaptive", text: "它会根据您的网络自动调整，充分利用带宽。" },
      { id: "live", text: "预览图先上传，您的画廊几分钟就能上线，原图随后跟上。" },
      { id: "resume", text: "连接中断后，它会自动续传。" },
    ],
    button: "打开网页上传工具",
    link: "了解全新网页上传工具",
  },
  what: {
    heading: "需要时，就有您自己的 FTP 服务器",
    body: "所有计划的工作区成员都可以在网页上传工具中启动自己的 FTP 服务器。每人一台服务器，使用永不改变的个人地址，所以 FTP 软件只需设置一次。不用时什么都不会运行：闲置一小时后它会自动关闭。",
    date: "2026年11月1日",
    closing: "我们一直在线的共享 FTP 服务器将于 2026年11月1日关闭。如果您现在还在用，请启动自己的服务器，并在 FTP 软件中修改主机地址。",
  },
  steps: {
    heading: "使用步骤",
    subtitle: "第一次需要四步。之后只需启动服务器，然后上传。",
    items: [
      { id: "open", title: "打开网页上传工具", body: "访问 `upload.photohawk.com` 并登录，或打开某个画廊的 «Upload» 标签页。" },
      { id: "start", title: "启动服务器", body: "点击顶部的 «FTP server»，再点击 «Start FTP server»。约 30 秒即可就绪。" },
      { id: "connect", title: "把地址和登录信息复制到 FTP 软件中", body: "您的地址、端口 21、您的 Photohawk 邮箱和密码。地址永远不会改变。" },
      { id: "upload", title: "上传", body: "把照片放进画廊对应的文件夹。一小时内没有上传，服务器就会关闭。" },
    ],
    button: "启动您的 FTP 服务器",
    buttonNote: "打开网页上传工具，您的 FTP 服务器已准备好启动。",
  },
  panel: {
    heading: "您会看到什么",
    body: "所有信息都在网页上传工具的 «Your FTP server» 面板中，每一项旁边都有复制按钮。",
    points: [
      { id: "address", text: "您的地址，例如 `swift-heron-4821.ftp.photohawk.com`。它永久属于您，所以只需设置一次。" },
      { id: "ip", text: "面板也会显示服务器的 IP 地址。每次启动服务器时 IP 都会变化，所以请尽量使用地址。" },
      { id: "countdown", text: "距离自动关闭的倒计时，以及 «Keep it running» 和 «Stop» 按钮。" },
    ],
    alt: "“Your FTP server”面板，显示欧洲（爱尔兰）的服务器状态为 Ready，以及主机 swift-heron-4821.ftp.photohawk.com、IP 地址、端口 21、用户名 alex@example.com、被动模式和 FTP over TLS，还有带 Keep it running 和 Stop 按钮的倒计时。",
  },
  details: {
    heading: "您的连接信息",
    subtitle: "在 FTP 软件中填写以下信息。",
    rows: [
      { id: "host", label: "主机", value: "您的地址，例如 `swift-heron-4821.ftp.photohawk.com`（或显示的 IP 地址）" },
      { id: "port", label: "端口", value: "21" },
      { id: "user", label: "用户名", value: "您的 Photohawk 邮箱" },
      { id: "password", label: "密码", value: "您的 Photohawk 密码，或您的 FTP 密码（见下文）" },
      { id: "mode", label: "传输模式", value: "被动" },
      { id: "encryption", label: "加密", value: "普通 FTP，或 FTPS（显式 FTP over TLS）。使用 FTPS 时，请填主机名，而不是 IP 地址。" },
    ],
    switching: "以前用过 Photohawk FTP？用户名、密码和文件夹都不变，只需把主机改成您的新地址。",
  },
  password: {
    heading: "如果需要，可以设置 FTP 专用密码",
    body: "不想把 Photohawk 密码保存在 FTP 软件里？在面板中点击 «Create FTP password» 即可。这个密码只能用于您的 FTP 服务器，用户名是您的邮箱。您可以随时复制它、生成新密码（旧密码会在一分钟内失效）或删除它。您的 Photohawk 密码也照常可用。",
  },
  folders: {
    heading: "您的文件夹",
    body: "您会看到和以前一样的文件夹：",
    path: "/Galleries/<gallery>/<collection>",
    points: [
      { id: "single", text: "只有一个合集的画廊，路径就是 `/Galleries/<gallery>`。" },
      { id: "workspaces", text: "要上传到多个工作区？每个工作区都有自己的文件夹，里面是它的画廊。" },
      { id: "create", text: "请先在 Photohawk 中创建画廊及其合集。照片会在片刻之内出现在画廊中，和用网页上传工具一样。" },
    ],
  },
  off: {
    heading: "它会自动关闭",
    body: "您的服务器在闲置 1 小时后会自动关闭，所以无需记着去关。面板会显示倒计时。",
    points: [
      { id: "keep", text: "«Keep it running» 会重新开始倒计时，无需上传任何内容。" },
      { id: "stop", text: "完成后点 «Stop»，服务器会立即关闭。" },
      { id: "caps", text: "为了安全起见，如果 12 小时内没有上传，它也会关闭；最长运行 24 小时。" },
      { id: "again", text: "随时可以重新启动，地址和登录信息不变。" },
    ],
  },
  regions: {
    heading: "区域",
    body: "您的服务器可以运行在欧洲（爱尔兰）、美国东部（弗吉尼亚北部）或亚太地区（马来西亚）。系统会为您选择最近的区域，启动前也可以更改。",
  },
  filezilla: {
    heading: "示例：设置 FileZilla",
    subtitle: "其他 FTP 软件使用相同的设置，只是名称有时略有不同。",
    steps: [
      { id: "site", text: "打开 «File → Site Manager»，点击 «New site»。" },
      { id: "protocol", text: "协议：«FTP – File Transfer Protocol»。" },
      { id: "host", text: "主机：您的地址，例如 `swift-heron-4821.ftp.photohawk.com`。端口：`21`。" },
      { id: "tls", text: "加密：«Use explicit FTP over TLS if available»。" },
      { id: "login", text: "登录类型：«Normal»。用户：您的 Photohawk 邮箱。密码：您的 FTP 密码或 Photohawk 密码。" },
      { id: "passive", text: "在 «Transfer Settings» 标签页中选择 «Passive»。点击 «Connect»，然后打开 `Galleries`。" },
    ],
    tip: "要发送一个大文件夹？FileZilla 默认同时传 2 个文件，最多不超过 10 个。您可以把它的同时传输数设置调高到 10（«Settings → Transfers»），或者使用网页上传工具，它能同时发送多得多的照片。",
  },
  trouble: {
    heading: "遇到问题时",
    items: [
      { id: "connect", title: "无法连接", body: "确认面板显示 «Ready»。如果主机名刚刚创建，请先用 IP 地址连接一分钟。" },
      { id: "listing", title: "已连接，但文件夹列表一直加载不出来", body: "请开启被动模式。" },
      { id: "logins", title: "“Too many failed logins”", body: "等待 15 分钟，然后检查用户名（您的邮箱）和密码。" },
    ],
  },
  faq: {
    heading: "常见问题",
    items: [
      {
        q: "为什么我的服务器关闭了？",
        a: "闲置一小时后它会自动关闭，这样您不用时它就不会运行。随时可以重新启动：地址和登录信息不变。如果想在不上传的情况下保持运行，请点击 «Keep it running»。另外，如果 12 小时内没有上传，它也会关闭；最长运行 24 小时。",
      },
      {
        q: "两个人可以同时运行服务器吗？",
        a: "可以。工作区里的每个人都可以启动自己的服务器，每人一台，各用自己的地址。只有您能登录您的服务器。",
      },
      {
        q: "安全吗？",
        a: "安全。在主机名上使用 FTPS（FTP over TLS）时，连接会通过受信任的证书加密。您的服务器只接受您的登录信息，多次登录失败后，服务器会把该连接封锁 15 分钟。",
      },
      {
        q: "需要额外付费吗？",
        a: "不需要。所有计划都包含您自己的 FTP 服务器。",
      },
      {
        q: "所有计划都能用吗？",
        a: "可以，Lite 也包括在内。日常上传用网页上传工具或 Photohawk Studio 更快；必须使用 FTP 时，可以启动您自己的 FTP 服务器。",
      },
      {
        q: "原来的共享 FTP 会怎样？",
        a: "一直在线的共享 FTP 服务器将于 2026年11月1日关闭，在此之前照常运行。如果想继续使用 FTP，请启动自己的服务器，并把 FTP 软件中的主机改成您的新地址。用户名、密码和文件夹都不变。",
      },
    ],
  },
  cta: {
    heading: "随时可以开始",
    body: "日常上传请使用网页上传工具。一定要用 FTP 时，您自己的服务器一键即可启动。",
    primary: "打开网页上传工具",
    secondary: "启动您的 FTP 服务器",
  },
};

export default zh;
